import { inject, Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import { ParentMessage } from '../model/environment/environment-data';
import { setDataFromIFrameMessage } from '../ngrx/store/environment/environment.actions';

/**
 * Service for iframe message handling
 * Initialized once at app start and responds ONLY to messages when the current route is '/dashboard'
 */
@Injectable({ providedIn: 'root' })
export class IframeMessageService {
  private readonly ngZone = inject(NgZone);
  private readonly store$ = inject(Store);
  private readonly router = inject(Router);

  private currentRoute = '';

  initialize() {
    // Route tracking for faster checks (without router check on every message)
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentRoute = event.urlAfterRedirects;
    });

    // Set initial route
    this.currentRoute = this.router.url;

    // Register event listener outside Angular zone (performance)
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('message', this.handleIFrameMessage.bind(this), false);
    });

    console.log('IframeMessageService initialized - listening for messages on /dashboard route only');
  }

  /**
   * Handles parent message of the iframe
   * Responds ONLY when the current route contains '/dashboard'
   * @param message of the parent
   */
  private handleIFrameMessage(message: MessageEvent) {
    // IMPORTANT: Early exit if not on dashboard or root route
    if (!['/dashboard', '/'].includes(this.currentRoute)) {
      return;
    }

    if (!message.data || message.data.id !== 'sirius') {
      return;
    }

    console.log(`Got ${message.data.id} Parent Message on route ${this.currentRoute}: ` + JSON.stringify(message.data));

    const parentMessage: ParentMessage = {
      baseUrl: message.data.base,
      deliveryGuid: message.data.guid,
      readOnly: message.data.readOnly,
      deliveryName: message.data.deliveryName,
      isLocked: message.data.isLocked,
      activationDate: message?.data?.activationDate ? new Date(message.data.activationDate) : null,
    };

    // Back to Angular zone for store dispatch
    this.ngZone.run(() => {
      this.store$.dispatch(setDataFromIFrameMessage({ environmentData: parentMessage }));
    });
  }
}
